import React, { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Tabs, Tab, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ProfileInfoTab from '../../pages/ProfilePage/tabs/ProfileInfoTab';
import ProfileLeavesTab from '../../pages/ProfilePage/tabs/ProfileLeavesTab';
import ProfileExpensesTab from '../../pages/ProfilePage/tabs/ProfileExpensesTab';
import { useProfile } from '../../hooks';

const tabConfig = [
  {
    value: 'profile',
    labelKey: 'profile.tabs.info',
    defaultLabel: 'Profil Bilgileri',
    icon: <PersonIcon sx={{ mr: 1, mb: '-2px' }} />,
    component: ProfileInfoTab,
  },
  {
    value: 'leaves',
    labelKey: 'profile.tabs.leaves',
    defaultLabel: 'İzinler',
    icon: <EventNoteIcon sx={{ mr: 1, mb: '-2px' }} />,
    component: ProfileLeavesTab,
  },
  {
    value: 'expenses',
    labelKey: 'profile.tabs.expenses',
    defaultLabel: 'Masraflar',
    icon: <ReceiptIcon sx={{ mr: 1, mb: '-2px' }} />,
    component: ProfileExpensesTab,
  },
];

const UserProfileViewModal = ({ open, onClose, userId, userName }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(tabConfig[0].value);
  const { fetchProfile } = useProfile();

  useEffect(() => {
    if (open && userId) {
      fetchProfile(userId);
    }
  }, [open, userId, fetchProfile]);

  const activeTabObj = useMemo(
    () => tabConfig.find(tab => tab.value === activeTab),
    [activeTab]
  );

  const handleClose = () => {
    setActiveTab(tabConfig[0].value); 
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            {t('users.actions.viewProfile', 'Kullanıcı Profili')}
          </Box>
          {userName && (
            <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
              - {userName}
            </Box>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { pt: 0 } }}>
        <Box sx={{ py: 2, px: { xs: 1, sm: 3 } }}>
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 3, bgcolor: 'background.paper', p: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="profile tabs"
              TabIndicatorProps={{ style: { height: 4, borderRadius: 2 } }}
            >
              {tabConfig.map(tab => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {tab.icon}
                      <span>{t(tab.labelKey, tab.defaultLabel)}</span>
                    </Box>
                  }
                  sx={{ minWidth: 140, fontWeight: activeTab === tab.value ? 700 : 400, borderRadius: 2, mx: 0.5 }}
                />
              ))}
            </Tabs>
          </Paper>
          <Box>
            {activeTabObj?.component && userId && (
              <activeTabObj.component userId={userId} />
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileViewModal;

